import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, SearchLg, Edit05, Trash01, Eye } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import VehicleForm from "./VehicleForm";
import {
  getVehicles,
  createVehicle,
  updateVehicle,
  deleteVehicle,
  type CreateVehiclePayload,
  type VehicleWithCustomer,
} from "@/api/vehicles";
import { getCustomers } from "@/api/customers";
import type { CustomerDto, PaginatedResponse } from "@/types/dto";

const CAN_CREATE = ["admin", "owner", "manager", "receptionist"];
const CAN_EDIT = ["admin", "owner", "manager"];
const CAN_DELETE = ["admin", "owner"];

export default function VehiclesListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [result, setResult] = useState<PaginatedResponse<VehicleWithCustomer> | null>(null);
  const [customers, setCustomers] = useState<CustomerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [formOpen, setFormOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<VehicleWithCustomer | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const canCreate = CAN_CREATE.includes(user?.role ?? "");
  const canEdit = CAN_EDIT.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getVehicles({ page, limit: 20, search: search || undefined });
      setResult(data);
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await getCustomers({ limit: 100 });
      setCustomers(data.data);
    } catch {
      // handled by interceptor
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleCreate = async (payload: CreateVehiclePayload) => {
    setSaving(true);
    try {
      await createVehicle(payload);
      setFormOpen(false);
      fetchVehicles();
      toast.success("Vehicle created");
    } catch {
      toast.error("Failed to create vehicle");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (payload: CreateVehiclePayload) => {
    if (!editingVehicle) return;
    setSaving(true);
    try {
      await updateVehicle(editingVehicle.id, payload);
      setEditingVehicle(null);
      setFormOpen(false);
      fetchVehicles();
      toast.success("Vehicle updated");
    } catch {
      toast.error("Failed to update vehicle");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    setSaving(true);
    try {
      await deleteVehicle(deleteConfirmId);
      setDeleteConfirmId(null);
      fetchVehicles();
      toast.success("Vehicle deleted");
    } catch {
      toast.error("Failed to delete vehicle");
    } finally {
      setSaving(false);
    }
  };

  const vehicles = result?.data ?? [];
  const pagination = result?.pagination;

  return (
    <PageLayout
      title="Vehicles"
      subtitle="Manage registered vehicles"
      actions={
        canCreate ? (
          <Button
            size="md"
            color="primary"
            iconLeading={Plus}
            onClick={() => {
              setEditingVehicle(null);
              setFormOpen(true);
            }}
          >
            Add Vehicle
          </Button>
        ) : undefined
      }
    >
      <div className="mb-4 sm:mb-6 max-w-sm">
        <Input
          placeholder="Search by make, model, plate..."
          icon={SearchLg}
          value={search}
          onChange={setSearch}
        />
      </div>

      {/* Empty / Loading state */}
      {loading && vehicles.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">Loading...</div>
      ) : vehicles.length === 0 ? (
        <div className="py-12 text-center text-text-tertiary">
          {search ? "No vehicles match your search." : "No vehicles yet."}
        </div>
      ) : (
        <>
          {/* Mobile Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 xl:hidden">
            {vehicles.map((v) => (
              <div
                key={v.id}
                className="rounded-xl border border-border-secondary bg-bg-primary p-4"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/vehicles/${v.id}`)}
                  >
                    <p className="font-medium text-text-primary">
                      {v.make} {v.model}
                    </p>
                    <p className="text-sm text-text-secondary mt-0.5">
                      {v.year} &middot; {v.licensePlate}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 ml-2">
                    {canEdit && (
                      <Button
                        size="sm"
                        color="link-gray"
                        iconLeading={Edit05}
                        onClick={() => {
                          setEditingVehicle(v);
                          setFormOpen(true);
                        }}
                        aria-label="Edit"
                      />
                    )}
                    {canDelete && (
                      <Button
                        size="sm"
                        color="link-destructive"
                        iconLeading={Trash01}
                        onClick={() => setDeleteConfirmId(v.id)}
                        aria-label="Delete"
                      />
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-text-tertiary">
                  {v.color && <span>{v.color}</span>}
                  {v.mileage != null && <span>{v.mileage.toLocaleString()} km</span>}
                  {v.customer && (
                    <span>{v.customer.firstName} {v.customer.lastName}</span>
                  )}
                  {!v.customer && <span>No owner</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table */}
          <div className="hidden xl:block overflow-x-auto rounded-xl border border-border-secondary">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Vehicle</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Year</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Reg No</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Customer</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Color</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Mileage</th>
                  <th className="px-6 py-3 text-right font-medium text-text-tertiary">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr
                    key={v.id}
                    className="border-b border-border-secondary last:border-0 hover:bg-bg-secondary_hover transition-colors"
                  >
                    <td className="px-6 py-4 font-medium text-text-primary">
                      {v.make} {v.model}
                    </td>
                    <td className="px-6 py-4 text-text-secondary">{v.year}</td>
                    <td className="px-6 py-4 text-text-secondary">{v.licensePlate}</td>
                    <td className="px-6 py-4 text-text-secondary">
                      {v.customer
                        ? `${v.customer.firstName} ${v.customer.lastName}`
                        : "No owner"}
                    </td>
                    <td className="px-6 py-4 text-text-secondary">{v.color ?? "—"}</td>
                    <td className="px-6 py-4 text-text-secondary">
                      {v.mileage != null ? v.mileage.toLocaleString() : "—"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          color="link-gray"
                          iconLeading={Eye}
                          onClick={() => navigate(`/vehicles/${v.id}`)}
                          aria-label="View"
                        />
                        {canEdit && (
                          <Button
                            size="sm"
                            color="link-gray"
                            iconLeading={Edit05}
                            onClick={() => {
                              setEditingVehicle(v);
                              setFormOpen(true);
                            }}
                            aria-label="Edit"
                          />
                        )}
                        {canDelete && (
                          <Button
                            size="sm"
                            color="link-destructive"
                            iconLeading={Trash01}
                            onClick={() => setDeleteConfirmId(v.id)}
                            aria-label="Delete"
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-tertiary">
            Page {pagination.page} of {pagination.totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              color="secondary"
              isDisabled={pagination.page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              size="sm"
              color="secondary"
              isDisabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingVehicle(null);
        }}
      >
        <DialogTitle>{editingVehicle ? "Edit Vehicle" : "Add Vehicle"}</DialogTitle>
        <DialogDescription>
          {editingVehicle
            ? "Update the vehicle's information below."
            : "Fill in the details to register a new vehicle."}
        </DialogDescription>
        <VehicleForm
          key={editingVehicle?.id ?? "new"}
          customers={customers}
          defaultValues={editingVehicle ?? undefined}
          onSubmit={editingVehicle ? handleEdit : handleCreate}
          onCancel={() => {
            setFormOpen(false);
            setEditingVehicle(null);
          }}
          isLoading={saving}
        />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
      >
        <DialogTitle>Delete Vehicle</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this vehicle? This action cannot be undone.
        </DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button
            size="md"
            color="primary-destructive"
            isLoading={saving}
            isDisabled={saving}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
